import { ref, computed, provide, watch, nextTick, openBlock, createElementBlock, normalizeClass, createElementVNode, renderSlot, createBlock, Teleport, createVNode, Transition, withCtx, normalizeStyle, unref, createCommentVNode } from "vue";
import { useClickOutside } from "../uikit.utils/useClickOutside.es.js";
import { debounce } from "../uikit.helpers/debounce.es.js";
import { useElementHover } from "../uikit.utils/useElementHover.es.js";
/* empty css                 */
import _export_sfc from "../uikit._virtual/_plugin-vue_export-helper.es.js";
const DROPDOWN_PLACEMENT = {
  BOTTOM_LEFT: "bottomLeft",
  BOTTOM: "bottom",
  BOTTOM_RIGHT: "bottomRight",
  TOP_LEFT: "topLeft",
  TOP: "top",
  TOP_RIGHT: "topRight"
};
const DROPDOWN_TRIGGER = {
  CLICK: "click",
  HOVER: "hover",
  NONE: "none"
};
const DEFAULT_DROPDOWN_POSITION = { top: "-900px", left: "-900px" };
const _sfc_main = {
  __name: "Dropdown",
  props: {
    placement: {
      type: String,
      default: DROPDOWN_PLACEMENT.BOTTOM_LEFT,
      validator(placement) {
        return Object.values(DROPDOWN_PLACEMENT).includes(placement);
      }
    },
    trigger: {
      type: String,
      default: DROPDOWN_TRIGGER.HOVER,
      validator(placement) {
        return Object.values(DROPDOWN_TRIGGER).includes(placement);
      }
    },
    modelValue: {
      type: Boolean,
      default: false
    },
    dropDownClass: {
      type: String,
      default: ""
    },
    forceCloseOnClick: {
      type: Boolean,
      default: false
    },
    autoHeight: {
      type: Boolean,
      default: false
    }
  },
  emits: ["onOpen", "onClose", "update:modelValue"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const dropdown = ref();
    const dropdownTrigger = ref();
    const dropdownRoot = ref();
    const refreshIncrementHack = ref(0);
    const isVisible = ref(false);
    const dropdownPosition = ref(DEFAULT_DROPDOWN_POSITION);
    const dropdownPlacementModifier = ref(null);
    const isDropdownTriggerHover = useElementHover(dropdownTrigger);
    const isDropdownHover = useElementHover(dropdown);
    const isTop = computed(
      () => [DROPDOWN_PLACEMENT.TOP_LEFT, DROPDOWN_PLACEMENT.TOP, DROPDOWN_PLACEMENT.TOP_RIGHT].includes(
        props.placement
      )
    );
    const isCentered = computed(
      () => [DROPDOWN_PLACEMENT.TOP, DROPDOWN_PLACEMENT.BOTTOM].includes(props.placement)
    );
    const isLeft = computed(
      () => [DROPDOWN_PLACEMENT.TOP_LEFT, DROPDOWN_PLACEMENT.BOTTOM_LEFT].includes(props.placement)
    );
    const isRight = computed(
      () => [DROPDOWN_PLACEMENT.TOP_RIGHT, DROPDOWN_PLACEMENT.BOTTOM_RIGHT].includes(props.placement)
    );
    const heightKey = computed(() => props.autoHeight ? "maxHeight" : "height");
    const transitionType = computed(() => {
      let top;
      if (dropdownPlacementModifier.value) {
        top = dropdownPlacementModifier.value === DROPDOWN_PLACEMENT.TOP;
      } else {
        top = isTop.value;
      }
      return `slide-${top ? "down" : "up"}`;
    });
    const setVisible = () => {
      if (isVisible.value) {
        return;
      }
      isVisible.value = true;
      emit("onOpen");
      emit("update:modelValue", true);
    };
    const setInvisible = debounce((force = false) => {
      if (!force && (!isVisible.value || isDropdownHover.value || isDropdownTriggerHover.value)) {
        return;
      }
      isVisible.value = false;
      emit("onClose");
      emit("update:modelValue", false);
    }, 100);
    const handleTriggerClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (props.trigger !== DROPDOWN_TRIGGER.CLICK) {
        return;
      }
      if (!isVisible.value) {
        setVisible();
      } else {
        setInvisible(props.forceCloseOnClick);
      }
    };
    const handleTriggerHover = (hover) => {
      if (props.trigger !== DROPDOWN_TRIGGER.HOVER) {
        return;
      }
      if (hover) {
        setVisible();
      } else {
        setInvisible();
      }
    };
    const resetPosition = () => {
      if (!isVisible.value) {
        dropdownPosition.value = DEFAULT_DROPDOWN_POSITION;
      }
    };
    __expose({
      hide: () => setInvisible(true),
      refreshPosition: () => {
        refreshIncrementHack.value++;
      }
    });
    const closeDropDown = () => {
      setInvisible(true);
    };
    provide("closeDropDown", closeDropDown);
    const { start, stop } = useClickOutside(dropdown, () => setInvisible(), false);
    watch(
      () => props.modelValue,
      (value, oldValue) => {
        if (value === oldValue) {
          return;
        }
        if (value) {
          setVisible();
        } else {
          setInvisible(true);
        }
      }
    );
    watch(isDropdownTriggerHover, handleTriggerHover);
    watch(isDropdownHover, handleTriggerHover);
    watch([isVisible, refreshIncrementHack], ([value]) => {
      if (value) {
        start();
        nextTick(() => {
          window.requestAnimationFrame(() => {
            dropdownPlacementModifier.value = null;
            const dropdownTriggerBoundingClientRect = dropdownTrigger.value.getBoundingClientRect();
            const dropdownTriggerRect = {
              left: dropdownTriggerBoundingClientRect.left,
              right: dropdownTriggerBoundingClientRect.right,
              width: dropdownTriggerBoundingClientRect.width,
              bottom: dropdownTriggerBoundingClientRect.bottom,
              top: dropdownTriggerBoundingClientRect.top,
              height: dropdownTriggerBoundingClientRect.height
            };
            const dropdownBoundingClientRect = dropdown.value.getBoundingClientRect();
            const dropdownRect = {
              minWidth: dropdownTriggerRect.width,
              width: dropdownBoundingClientRect.width,
              height: dropdown.value.offsetHeight
            };
            if (isLeft.value) {
              dropdownRect.left = dropdownTriggerRect.left;
            } else if (isRight.value) {
              dropdownRect.left = dropdownTriggerRect.right - dropdownBoundingClientRect.width;
            }
            if (isTop.value) {
              dropdownRect.top = dropdownTriggerRect.top - dropdownRect.height - 4;
            } else {
              dropdownRect.top = dropdownTriggerRect.bottom + 4;
            }
            if (isCentered.value) {
              dropdownTriggerRect.left = dropdownTriggerRect.left + dropdownTriggerRect.width / 2;
              dropdownTriggerRect.right = dropdownTriggerRect.right - dropdownTriggerRect.width / 2;
              dropdownRect.width = dropdownRect.width / 2;
              dropdownRect.left = dropdownTriggerRect.left - dropdownRect.width;
            }
            const hasOverflowLeft = (dropdownRect.left || window.innerWidth) < 0;
            const hasOverflowRight = (dropdownRect.left || 0) + dropdownRect.width > window.innerWidth;
            const hasOverflowTop = (dropdownTriggerRect.top - dropdownRect.height - 4 || window.innerHeight) < 0;
            const hasOverflowDown = (dropdownTriggerRect.bottom + 4 || 0) + dropdownRect.height > window.innerHeight;
            if (hasOverflowRight) {
              dropdownRect.left = void 0;
              dropdownRect.right = 4;
            }
            if (hasOverflowLeft) {
              dropdownRect.left = 4;
              dropdownRect.right = void 0;
            }
            if (hasOverflowDown) {
              dropdownRect.top = dropdownTriggerRect.top - dropdownRect.height - 4;
              dropdownPlacementModifier.value = DROPDOWN_PLACEMENT.TOP;
            }
            if (hasOverflowTop) {
              dropdownRect.top = dropdownTriggerRect.bottom + 4;
              dropdownPlacementModifier.value = DROPDOWN_PLACEMENT.BOTTOM;
            }
            if (dropdownRect.top + dropdownRect.height > window.innerHeight) {
              dropdownRect.height = window.innerHeight - dropdownRect.top - 12;
            }
            dropdownPosition.value = {
              top: dropdownRect.top + "px",
              left: dropdownRect.left ? dropdownRect.left + "px" : dropdownRect.left,
              right: dropdownRect.right ? dropdownRect.right + "px" : dropdownRect.right,
              minWidth: dropdownRect.minWidth + "px",
              [heightKey]: dropdownRect.height + "px"
            };
          });
        });
      } else {
        stop();
        setTimeout(resetPosition, 201);
      }
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "dropdownRoot",
        ref: dropdownRoot,
        class: normalizeClass(["uikit-drop-down", { open: isVisible.value }])
      }, [
        createElementVNode("div", {
          ref_key: "dropdownTrigger",
          ref: dropdownTrigger,
          class: "dropdown-trigger",
          onClick: handleTriggerClick
        }, [
          renderSlot(_ctx.$slots, "default", {}, void 0, true)
        ], 512),
        (openBlock(), createBlock(Teleport, { to: "body" }, [
          createVNode(Transition, { name: transitionType.value }, {
            default: withCtx(() => [
              isVisible.value ? (openBlock(), createElementBlock("div", {
                key: 0,
                ref_key: "dropdown",
                ref: dropdown,
                class: normalizeClass(["dropdown", __props.dropDownClass]),
                style: normalizeStyle(dropdownPosition.value)
              }, [
                createElementVNode("div", {
                  class: "dropdown-menu",
                  style: normalizeStyle({ height: dropdownPosition.value.height })
                }, [
                  renderSlot(_ctx.$slots, "overlay", { hide: unref(setInvisible) }, void 0, true)
                ], 4)
              ], 6)) : createCommentVNode("", true)
            ]),
            _: 3
          }, 8, ["name"])
        ]))
      ], 2);
    };
  }
};
const UikitDropdown = /* @__PURE__ */ _export_sfc(_sfc_main, [["__scopeId", "data-v-c9edb565"]]);
export {
  UikitDropdown as default
};
//# sourceMappingURL=Dropdown.vue.es.js.map
